import { ListResponse } from "@/web-client/api";
import EditListItemContext from "@/contexts/EditListItemContext";
import useDisclosure from "@/hooks/useDisclosure";
import useMyself from "@/features/user/hooks/useMyself";
import { FC, useContext, useMemo } from "react";
import ShareBtn from "@/components/ShareBtn";
import AvatarWithLabel from "@/components/AvatarWithLabel";
import { AvatarSize } from "@/components/Avatar";
import Maybe from "@/components/Maybe";
import ModalEditList from "@/features/list/components/ModalEditList";
import Button from "@/components/Button";

interface Props {
  list: ListResponse;
}

const ListProfile: FC<Props> = ({ list }): JSX.Element => {
  const isMyself = useMyself(list.user.id);
  const { isOpen, openHandler, closeHandler } = useDisclosure();
  const { isEditing, editHandler, editCompleteHandler } =
    useContext(EditListItemContext);

  const listOwner = useMemo<{
    imgSrc?: string;
    href?: string;
    name?: string;
  }>(() => {
    if (list.maker) {
      return {
        name: list.maker.name,
        imgSrc: list.maker?.icon?.urls.small,
        href: `/makers/${list.maker.id}`,
      };
    }

    return {
      imgSrc: list.user?.icon.urls.small,
      name: list.user?.screen_name,
      href: `/users/${list.user?.id}/lists`,
    };
  }, [list]);

  return (
    <div className="flex flex-col justify-between">
      <div className="flex items-center justify-between flex-col space-y-[28px] px-16">
        <div className="flex flex-col laptop:items-center gap-48 max-w-[640px] break-all">
          <div className="flex flex-col items-center gap-12">
            <div className="flex flex-col items-center gap-8">
              <Maybe condition={list.is_private}>
                <span className="space-x-8">
                  <i className="icon-Lock" />
                  <span className="text-sm">非公開</span>
                </span>
              </Maybe>

              <div className="flex items-center gap-8">
                <h1 className="font-bold laptop:text-2xl text-xl">
                  {list.name}
                </h1>
                <ShareBtn />
              </div>
            </div>

            <div className="gap-8 flex font-bold items-center text-xs">
              <span className="text-secondary">Created by</span>
              <AvatarWithLabel
                {...listOwner}
                imgSrc={listOwner.imgSrc || "/imgs/default_user_icon.png"}
                size={AvatarSize.Small}
                hasName
              />
            </div>
          </div>

          <Maybe condition={!!list.description}>
            <p className="gray-500 text-sm leading-loose whitespace-pre-wrap text-justify">
              {list.description}
            </p>
          </Maybe>
        </div>

        <Maybe condition={isMyself}>
          <div className="flex gap-4">
            {!isEditing ? (
              <>
                <Button onClick={openHandler} size="small">
                  編集
                </Button>

                <Button onClick={editHandler} size="small">
                  整理
                </Button>
              </>
            ) : (
              <Button onClick={editCompleteHandler} size="small">
                完了
              </Button>
            )}
          </div>
        </Maybe>
      </div>

      <Maybe condition={isOpen}>
        <ModalEditList
          list={list}
          isOpen={isOpen}
          closeHandler={closeHandler}
        />
      </Maybe>
    </div>
  );
};

export default ListProfile;
